import { Box, Stack, ThemeProvider, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Icon,
  ANNOUNCEMENT_TYPE,
  DISMISS_BEHAVIOR,
} from 'bs-unified-ui';
import { useEffect, useRef, useState } from 'react';

type Props = {
  announcement: any;
  onGenerated?: (element: HTMLElement) => void;
};

/**
 * Component for rendering announcement content specifically for thumbnail generation
 */
const ThumbnailContent = ({ announcement }: { announcement: any }) => {
  const theme = useTheme();
  const [imageDataUrl, setImageDataUrl] = useState<string>('');

  // Convert File object to data URL for thumbnail generation
  useEffect(() => {
    const { image } = announcement;

    if (image instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImageDataUrl(e.target.result as string);
        }
      };
      reader.readAsDataURL(image);
    } else if (typeof image === 'string') {
      setImageDataUrl(image);
    } else {
      setImageDataUrl('');
    }
  }, [announcement]);

  const renderHomeBanner = () => {
    const {
      type,
      message_title,
      message_description,
      banner_color,
      icon,
      dismiss_behaviors,
    } = announcement;
    if (type === ANNOUNCEMENT_TYPE.HOME_PAGE_BANNER)
      return (
        <Stack
          sx={{
            backgroundColor: banner_color,
            px: 3,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            borderRadius: '4px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              fontWeight={700}
              variant='fs16'
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {icon !== 'ch-close-fill' && <Icon name={icon} size={16} unset />}
              {message_title}
            </Typography>
            {dismiss_behaviors !== DISMISS_BEHAVIOR.NON_DISMISSABLE && (
              <CloseIcon sx={{ fontSize: 12, color: '#10111E' }} />
            )}
          </Box>
          <Box
            sx={{
              'mt': 0.25,
              '& p': {
                margin: 0,
              },
              '& *': {
                fontFamily: '"Lato", sans-serif',
                lineHeight: '24px',
              },
              '& a': {
                'color': `${theme.palette.primary.main}`,
                '&:visited': {
                  color: `${theme.palette.primary.main}`,
                },
              },
            }}
            className='ql-editor'
            dangerouslySetInnerHTML={{
              __html: message_description,
            }}
          />
        </Stack>
      );
    return null;
  };

  const renderGlobalBanner = () => {
    const { type, message_description, banner_color, icon, dismiss_behaviors } =
      announcement;
    if (type === ANNOUNCEMENT_TYPE.GLOBAL_BANNER) {
      const isLightBackground = banner_color === '#EDF0F7';
      const textColor = isLightBackground ? '#10111E' : '#FFFFFF';
      const closeIconColor = isLightBackground ? '#10111E' : '#FFFFFF';

      return (
        <Stack
          sx={{
            backgroundColor: banner_color,
            p: 1,
            pr: 2,
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            {icon !== 'ch-close-fill' && <Icon name={icon} size={16} unset />}
            <Box
              sx={{
                'mt': 0.25,
                '& p': {
                  margin: 0,
                },
                '& *': {
                  color: textColor,
                  fontFamily: '"Lato", sans-serif',
                  lineHeight: '24px',
                },
              }}
              className='ql-editor'
              dangerouslySetInnerHTML={{
                __html: message_description,
              }}
            />
          </Box>
          {dismiss_behaviors !== DISMISS_BEHAVIOR.NON_DISMISSABLE && (
            <CloseIcon sx={{ fontSize: 12, color: closeIconColor }} />
          )}
        </Stack>
      );
    }
    return null;
  };

  const renderPopup = () => {
    const { type, message_title, message_description } = announcement;
    if (type === ANNOUNCEMENT_TYPE.POP_UP) {
      return (
        <Stack
          sx={{
            backgroundColor: 'white',
            borderRadius: '4px',
            minWidth: 400,
            color: theme.foreground.black,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderBottom: '1px solid #E2E7F0',
            }}
          >
            <Typography fontWeight={700} variant='fs14'>
              {message_title}
            </Typography>
            <CloseIcon sx={{ fontSize: 16, color: '#10111E' }} />
          </Box>
          <Box
            sx={{
              p: 2,
              pb: 0,
            }}
          >
            <Box
              sx={{
                '& p': {
                  margin: 0,
                },
                '& ol, ul': {
                  paddingLeft: '0 !important',
                },
                '& ol > li': {
                  paddingLeft: '1.2em !important',
                },
                '& ul > li': {
                  paddingLeft: '0.8em !important',
                },
                '& *': {
                  fontFamily: '"Lato", sans-serif',
                  lineHeight: '24px',
                },
                '& a': {
                  'color': `${theme.palette.primary.main}`,
                  '&:visited': {
                    color: `${theme.palette.primary.main}`,
                  },
                },
              }}
              className='ql-editor'
              dangerouslySetInnerHTML={{
                __html: message_description,
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 2,
            }}
          >
            <Button>Dismiss</Button>
          </Box>
        </Stack>
      );
    }
    return null;
  };

  const renderRichPopup = () => {
    const { type, message_title, message_description } = announcement;

    if (type === ANNOUNCEMENT_TYPE.RICH_POP_UP) {
      return (
        <Stack
          sx={{
            backgroundColor: 'white',
            borderRadius: '4px',
            color: theme.foreground.black,
            p: 2,
            height: '100%',
            justifyContent: 'space-between',
            maxWidth: 500,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <CloseIcon sx={{ fontSize: 16, color: '#10111E' }} />
          </Box>
          <Box
            sx={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 500,
              height: 300, // 500 * 3/5 = 300 to maintain 5:3 ratio
            }}
          >
            {imageDataUrl && (
              <img
                src={imageDataUrl}
                alt=''
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }}
                crossOrigin='anonymous'
              />
            )}
          </Box>
          <Typography
            fontWeight={700}
            variant='fs20'
            sx={{
              mt: 2,
              textAlign: 'center',
            }}
          >
            {message_title}
          </Typography>
          <Box
            sx={{
              py: 2,
            }}
          >
            <Box
              sx={{
                '& p': {
                  margin: 0,
                },
                '& ol, ul': {
                  paddingLeft: '0 !important',
                },
                '& ol > li': {
                  paddingLeft: '1.2em !important',
                },
                '& ul > li': {
                  paddingLeft: '0.8em !important',
                },
                '& *': {
                  fontFamily: '"Lato", sans-serif',
                  lineHeight: '24px',
                },
                '& a': {
                  'color': `${theme.palette.primary.main}`,
                  '&:visited': {
                    color: `${theme.palette.primary.main}`,
                  },
                },
              }}
              className='ql-editor'
              dangerouslySetInnerHTML={{
                __html: message_description,
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button>Dismiss</Button>
          </Box>
        </Stack>
      );
    }
    return null;
  };

  return (
    <>
      {renderHomeBanner()}
      {renderGlobalBanner()}
      {renderPopup()}
      {renderRichPopup()}
    </>
  );
};

const ThumbnailGenerator = ({ announcement, onGenerated }: Props) => {
  const theme = useTheme();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const hasGeneratedRef = useRef(false);

  useEffect(() => {
    if (!elementRef.current || !onGenerated || hasGeneratedRef.current) return;

    // Add a small delay to ensure FileReader has finished converting to data URL
    const delayTimer = setTimeout(() => {
      if (!elementRef.current) return;

      const el = elementRef.current;
      const images = el.querySelectorAll('img');

      if (images.length === 0) {
        hasGeneratedRef.current = true;
        onGenerated(el);
      } else {
        // Wait for all images to load
        Promise.all(
          Array.from(images).map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete && img.naturalHeight !== 0) {
                  resolve(null);
                } else {
                  img.onload = () => resolve(null);
                  img.onerror = () => {
                    console.error('Image failed to load:', img.src);
                    resolve(null);
                  };
                }
              }),
          ),
        ).then(() => {
          if (elementRef.current) {
            hasGeneratedRef.current = true;
            onGenerated(elementRef.current);
          }
        });
      }
    }, 300); // Wait 300ms for FileReader

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(delayTimer);
  }, [announcement, onGenerated]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        ref={elementRef}
        id='announcement-thumbnail-generator'
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: 'max-content',
          height: 'auto',
        }}
      >
        <ThumbnailContent announcement={announcement} />
      </Box>
    </ThemeProvider>
  );
};

export default ThumbnailGenerator;
